import React from "react";
import { Link } from "react-router-dom";
import { trackCTAClick } from "@/lib/analytics";

interface CTASectionProps {
  title: string;
  description: string;
  primaryButtonText?: string;
  primaryButtonHref?: string;
  secondaryButtonText?: string;
  secondaryButtonHref?: string;
  showSecondaryButton?: boolean;
  variant?: "default" | "dark" | "orange";
  className?: string;
  language?: "en" | "es";
}

const CTASection: React.FC<CTASectionProps> = ({
  title,
  description,
  primaryButtonText = "Set up a 15-min call",
  primaryButtonHref = "/book",
  secondaryButtonText = "Send a Message",
  secondaryButtonHref = "/contact",
  showSecondaryButton = true,
  variant = "default",
  className = "",
  language = "en",
}) => {
  const getBackgroundClass = () => {
    switch (variant) {
      case "dark":
        return "bg-dark-900";
      case "orange":
        return "bg-metro-orange-500";
      default:
        return "bg-metro-red-900";
    }
  };

  const getTextColorClass = () => {
    switch (variant) {
      case "orange":
        return "text-dark-950";
      default:
        return "text-white";
    }
  };

  const getDescriptionColorClass = () => {
    switch (variant) {
      case "dark":
        return "text-dark-300";
      case "orange":
        return "text-dark-900";
      default:
        return "text-metro-red-100";
    }
  };

  const isExternalLink = (href: string) => {
    return href.startsWith("http") || href.startsWith("mailto:");
  };

  const handlePrimaryClick = () => {
    trackCTAClick(primaryButtonText, primaryButtonHref, language);
  };

  const handleSecondaryClick = () => {
    trackCTAClick(secondaryButtonText, secondaryButtonHref, language);
  };

  return (
    <section
      className={`py-20 lg:py-32 ${getBackgroundClass()} ${getTextColorClass()} ${className}`}
    >
      <div className="container px-4 mx-auto text-center">
        <h2 className="mb-6 text-3xl font-bold lg:text-4xl">{title}</h2>
        <p
          className={`max-w-2xl mx-auto mb-8 text-xl ${getDescriptionColorClass()}`}
        >
          {description}
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          {isExternalLink(primaryButtonHref) ? (
            <a
              href={primaryButtonHref}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              onClick={handlePrimaryClick}
            >
              {primaryButtonText}
            </a>
          ) : (
            <Link
              to={primaryButtonHref}
              className="btn-primary"
              onClick={handlePrimaryClick}
            >
              {primaryButtonText}
            </Link>
          )}

          {showSecondaryButton && (
            <>
              {isExternalLink(secondaryButtonHref) ? (
                <a
                  href={secondaryButtonHref}
                  className="btn-secondary"
                  onClick={handleSecondaryClick}
                >
                  {secondaryButtonText}
                </a>
              ) : (
                <Link
                  to={secondaryButtonHref}
                  className="btn-secondary"
                  onClick={handleSecondaryClick}
                >
                  {secondaryButtonText}
                </Link>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default CTASection;
